// Global
import { GetStaticComponentProps } from '@sitecore-jss/sitecore-jss-nextjs';

// Lib
import graphQLClientFactory from 'lib/graphql-client-factory';

// Local
import { IngredientData } from '../Accordion/Accordion';

// Global
import { Text, TextField } from '@sitecore-jss/sitecore-jss-nextjs';
import React from 'react';

// Lib
import { ComponentProps } from 'lib/component-props';

// Local
import Container from 'components/authorable/Layout/DartContainer/DartContainer';
import RichTextA11yWrapper from 'helpers/RichTextA11yWrapper/RichTextA11yWrapper';
import ShareButton from 'helpers/ShareButton/ShareButton';
import { tailwindVariants } from './HeroIngredientTailwind';
import { useI18n } from 'next-localization';

export type HeroIngredientProps = ComponentProps & { ingredientData?: Ingredient };

type Ingredient = {
  item: IngredientData;
};
export type IngredientInfoPath = {
  item: {
    path: string;
  };
};

type Alignment = 'Left' | undefined;

const HeroIngredient = (props: HeroIngredientProps): JSX.Element => {
  const { item } = props?.ingredientData || {};

  // Component variants
  const { alignContent } = props?.params || {};
  const alignment = alignContent !== 'Left';

  const i18n = useI18n();
  const i18n_definition = i18n.t('Definition');

  const {
    base,
    totalWrapper,
    heroIngredientWrapper,
    heroIngredientTitle,
    heroIngredientDescription,
    heroIngredientDefinition,
    heroIngredientDescriptionWrapper,
    labelWrapper,
    shareWrapper,
  } = tailwindVariants({
    alignment: alignment as unknown as Alignment,
  });

  if (!item) return <>Ingredient Hero</>;

  return (
    <>
      <div className={base()} data-component="authorable/heroes/ingredienthero">
        <Container>
          <div className={totalWrapper()}>
            <div className={heroIngredientWrapper()}>
              <div className={labelWrapper()}>
                <div className={shareWrapper()}>
                  <ShareButton />
                </div>
              </div>
              {item?.title?.value && (
                <div>
                  <Text
                    className={heroIngredientTitle()}
                    encode={false}
                    field={{ value: item?.title?.value || '' } as TextField}
                    tag="h1"
                  />
                </div>
              )}
              {item?.definition?.value && (
                <div className={heroIngredientDescriptionWrapper()}>
                  <Text
                    className={heroIngredientDefinition()}
                    encode={false}
                    field={{ value: i18n_definition } as TextField}
                  />
                  <RichTextA11yWrapper
                    className={heroIngredientDescription()}
                    field={{ value: item?.definition?.value }}
                    tag="p"
                  />
                </div>
              )}
            </div>
          </div>
        </Container>
      </div>
    </>
  );
};

export default HeroIngredient;

export const getStaticProps: GetStaticComponentProps = async (rendering, layoutData, context) => {
  const graphQLClient = graphQLClientFactory({});
  const ingredientPath = context?.params?.requestPath ?? null;
  let data = null;
  if (ingredientPath) {
    const basePathId =
      layoutData?.sitecore?.route?.fields?.ingredientBasePath &&
      'id' in layoutData.sitecore.route.fields.ingredientBasePath
        ? layoutData.sitecore.route.fields.ingredientBasePath.id
        : null;

    if (basePathId) {
      const basePathItem = await graphQLClient.request<IngredientInfoPath>(
        ingredientBasePathQuery,
        {
          basePathId: basePathId,
          language: layoutData?.sitecore?.context?.language,
        }
      );
      const basePath = basePathItem?.item?.path;

      let attempts = 0;
      let isValidItem = false;
      const itemName =
        ingredientPath && Array.isArray(ingredientPath) ? ingredientPath?.pop() : ingredientPath;
      let folderPath = '';

      if (itemName) {
        const firstChar = itemName.charAt(0);
        folderPath = !isNaN(Number(firstChar)) ? '0' : firstChar;

        do {
          attempts++;
          const itemPath = basePath + '/' + folderPath + '/' + itemName;
          data = await graphQLClient.request<Ingredient>(query, {
            path: itemPath,
            params: rendering.params,
            language: layoutData?.sitecore?.context?.language,
          });
          isValidItem = !!data?.item;
          folderPath = 'Hashtag';
        } while (!isValidItem && attempts < 2);
      }
    }
  }

  return {
    context: layoutData?.sitecore?.route,
    ingredientData: data,
  };
};

const query = /* GraphQL */ `
  query ($path: String!, $language: String!) {
    item(path: $path, language: $language) {
      name
      ... on Ingredient {
        title {
          value
        }
        definition {
          value
        }
        active {
          value
        }
      }
    }
  }
`;

const ingredientBasePathQuery = /* GraphQL */ `
  query IngredientBasePathQuery($basePathId: String!, $language: String!) {
    item(path: $basePathId, language: $language) {
      path
    }
  }
`;
